import { capitalize } from 'lodash';
import { Checkbox, Tooltip } from '@mui/material';
import { ContentCopy, CalendarToday, FilterNone } from '@mui/icons-material';
import { Post } from 'lib/types';
import { NETWORKS } from 'lib/constants';
import {
  abbreviateNum,
  buildPostPath,
  buildProjectPath,
} from 'lib/helpers';
import { MetricProps } from 'components/PostMetrics/types';
import { MobileTooltip } from 'components/MobileTooltip';
import { getAllowedMetrics } from 'components/PostMetrics/helpers';
import { formatLocalString } from 'lib/utils/datetime';
import { PostCaption } from 'components/PostCaption';
import { NetworkIcon } from 'elements/NetworkIcon';
import { A } from 'components/A';
import { PostImg } from 'components/PostImg';
import Link from 'next/link';
import { TagsCol } from './TagsCol';
import { CreditsCol } from './CreditsCol';
import { Method } from './AddDrawer';

interface Props {
  post: Post;
  selected?: boolean;
  drawerOpen?: boolean;
  method?: Method;
  onChange?: (post: Post, matchId?: string) => void;
  onCopy?: () => void;
  isMobile?: boolean;
  activeMetrics?: MetricProps[];
  selectionDisabled?: boolean;
}

export const PostRow = ({
  post,
  selected = false,
  drawerOpen = false,
  method,
  onChange,
  onCopy,
  isMobile = false,
  activeMetrics = [],
  selectionDisabled = false,
}: Props) => {
  const postAccount = post.account;
  const postDetailHref = buildPostPath(post);
  // Use hydrated projects from search results (defaults to empty array if not hydrated)
  const projects = post.projects || [];

  const renderMetrics = () => {
    if (isMobile) {
      const allowedMetrics = getAllowedMetrics(post.network, false);
      const metricData = allowedMetrics.map((metric: MetricProps) => {
        const metricValue = post[metric.field] as number;
        if (!metricValue && !metric.renderIfNull) {
          return null;
        }
        return (
          <div
            key={`post-${post.id}-${metric.field}`}
          >
            <MobileTooltip title={capitalize(metric.label)}>
              <p className="font-light">
                {metric.icon}
                &nbsp;
                {abbreviateNum(metricValue, 0)}
              </p>
            </MobileTooltip>
          </div>
        );
      });
      return (
        <div className="flex w-full flex-wrap min-w-[90px] gap-2 text-base">
          {metricData}
          {post.matchId && (
            <MobileTooltip title={`View all ${post.matchTotalCount} matches`}>
              <Link href={`/matches/${post.matchId}`} className="flex items-center gap-1 text-sm">
                <FilterNone fontSize="small" />
                {post.matchTotalCount}
              </Link>
            </MobileTooltip>
          )}
        </div>
      );
    }

    // Desktop view - render each metric in its own column
    return activeMetrics.map((metric: MetricProps) => {
      const metricValue = post[metric.field] as number;
      if (!metricValue && !metric.renderIfNull) {
        return <td key={`post-${post.id}-${metric.field}`} className="py-2 px-2 align-top border-l border-lightgray">-</td>;
      }
      return (
        <td key={`post-${post.id}-${metric.field}`} className="py-2 px-2 align-top border-l border-lightgray">
          <Tooltip title={capitalize(metric.label)}>
            <div>
              <span>
                {new Intl.NumberFormat()
                  .format(metricValue)}
              </span>
            </div>
          </Tooltip>
        </td>
      );
    });
  };

  if (isMobile) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="text-center text-mediumgray">
            <MobileTooltip
              title={
                selectionDisabled
                  ? 'To select this post for bulk actions, you need to have imported the post, or own or manage the post\'s account'
                  : ''
              }
              disableHoverListener={!selectionDisabled}
            >
              <span>
                <Checkbox
                  checked={selected}
                  onChange={() => onChange?.(post, post.matchId)}
                  value={post.id}
                  disabled={selectionDisabled}
                />
              </span>
            </MobileTooltip>
            {method === 'copy' && (
              <button
                type="button"
                aria-label="copy"
                title="copy post data"
                onClick={onCopy}
              >
                <ContentCopy />
              </button>
            )}
          </div>
          <Tooltip title={`open on ${NETWORKS[post.network].title}`} placement="bottom-start">
            <A href={post.url}>
              <NetworkIcon network={post.network} iconColor="color" />
            </A>
          </Tooltip>
          <A href={postDetailHref} target="_blank" className="font-semibold text-sm">
            @
            {postAccount}
          </A>
          <div className="text-sm text-mediumgray flex items-center gap-1 ml-auto">
            <MobileTooltip title="Posted date">
              <>
                <CalendarToday fontSize="small" />
                {formatLocalString(post.postedAt)}
              </>
            </MobileTooltip>
          </div>
        </div>
        <div>
          <A href={postDetailHref} target="_blank" title={`open ${postAccount}`}>
            <PostImg post={post} />
          </A>
        </div>
        <div className="w-full justify-start">
          {renderMetrics()}
        </div>
        <div>
          <CreditsCol credits={post.credits} />
        </div>
        {!drawerOpen && (
          <div>
            <PostCaption networkId={post.network}>{post.caption}</PostCaption>
          </div>
        )}
        <div>
          <TagsCol allTags={post.tags} />
        </div>
      </div>
    );
  }

  return (
    <>
      <td className="py-2 px-2 align-top">
        <div className="flex items-start gap-2">
          <div className="text-center text-mediumgray">
            <MobileTooltip
              title={
                selectionDisabled
                  ? 'To select this post for bulk actions, you need to have imported the post, or own or manage the post\'s account'
                  : ''
              }
              disableHoverListener={!selectionDisabled}
            >
              <span>
                <Checkbox
                  checked={selected}
                  onChange={() => onChange?.(post, post.matchId)}
                  value={post.id}
                  disabled={selectionDisabled}
                />
              </span>
            </MobileTooltip>
            {method === 'copy' && (
              <button
                type="button"
                aria-label="copy"
                title="copy post data"
                onClick={onCopy}
              >
                <ContentCopy />
              </button>
            )}
          </div>
          <A href={postDetailHref} target="_blank" title={`open ${postAccount}`}>
            <PostImg post={post} />
          </A>
        </div>
      </td>
      <td className="py-2 px-2 align-top">
        <div className="flex flex-col h-full gap-2">
          <div className="flex items-center gap-2">
            <Tooltip title={`open on ${NETWORKS[post.network].title}`} placement="bottom-start">
              <A href={post.url}>
                <NetworkIcon network={post.network} iconColor="color" />
              </A>
            </Tooltip>
            <A href={postDetailHref} target="_blank" className="font-semibold text-base">
              @
              {postAccount}
            </A>
          </div>
          <div className="text-sm text-mediumgray flex items-center gap-1">
            <MobileTooltip title="Posted date">
              <>
                <CalendarToday fontSize="small" />
                {formatLocalString(post.postedAt)}
              </>
            </MobileTooltip>
            {post.matchId && (
              <MobileTooltip title={`View all ${post.matchTotalCount} matches`}>
                <Link href={`/matches/${post.matchId}`} className="flex items-center gap-1 text-sm">
                  •
                  <FilterNone fontSize="small" />
                  {post.matchTotalCount}
                </Link>
              </MobileTooltip>
            )}
          </div>
        </div>
      </td>
      {renderMetrics()}
      <td className="py-2 px-2 align-top border-l border-lightgray">
        <CreditsCol credits={post.credits} />
      </td>
      {!drawerOpen && (
        <td className="py-2 px-2 align-top max-w-[500px]">
          <PostCaption networkId={post.network}>{post.caption ? `${post.caption.slice(0, 50)}...` : ''}</PostCaption>
        </td>
      )}
      <td className="py-2 px-2 align-top">
        <TagsCol allTags={post.tags} />
      </td>
      <td className="py-2 px-2 align-top">
        <div className="flex flex-col gap-2 max-w-[350px] text-sm">
          {projects.map((p) => (
            <li>
              <Link href={buildProjectPath(p)} className="hover:underline">
                {p.name}
              </Link>
            </li>
          ))}
        </div>
      </td>
    </>
  );
};
